// frontend/src/components/cart/Shipping.js - Enhanced with delivery area persistence

import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { saveShippingInfo } from '../../slices/cartSlice';
import { toast } from 'react-toastify';
import CheckoutSteps from './CheckoutStep';
import axios from 'axios';
import {
    MapPin,
    Phone,
    Truck,
    AlertTriangle,
    Building,
    Globe,
    Map,
    ArrowRight,
    Home,
    RefreshCw,
    CheckCircle,
    Edit2,
    X
} from 'lucide-react';

const Shipping = () => {
    const { shippingInfo = {} } = useSelector(state => state.cartState);
    const { user, loading: authLoading, isAuthenticated } = useSelector(state => state.authState);
    
    // Initialize state with shipping info or default values
    const [postalCode, setPostalCode] = useState(shippingInfo.postalCode || '');
    const [address, setAddress] = useState(shippingInfo.address || '');
    const [phoneNo, setPhoneNo] = useState(shippingInfo.phoneNo || '');
    const [loading, setLoading] = useState(false);
    
    // Delivery area management
    const [deliveryInfo, setDeliveryInfo] = useState(null);
    const [pincodeLoading, setPincodeLoading] = useState(false);
    const [pincodeError, setPincodeError] = useState('');
    const [manualPincode, setManualPincode] = useState('');
    const [showManualInput, setShowManualInput] = useState(false);
    
    // Use refs to track if fields have been auto-filled already
    const fieldsAutoFilledRef = useRef(false);
    
    // Default values
    const city = 'Bengaluru';
    const state = 'Karnataka';
    const country = 'India';
    
    // Updated serving areas based on client requirements
    const servingAreas = [
        { code: '560098', area: 'Rajarajeshwari Nagar', hubPlace: 'Kengeri' },
        { code: '560060', area: 'Kengeri', hubPlace: 'Kengeri' },
        { code: '560060', area: 'Viswaneedam', hubPlace: 'Segehalli' },
        { code: '560056', area: 'Bangalore Viswavidalaya', hubPlace: 'Kengeri' },
        { code: '562130', area: 'Seegehalli', hubPlace: 'Segehalli' }
    ];

    const dispatch = useDispatch();
    const navigate = useNavigate();
    
    // Load saved delivery data on component mount (CRITICAL FIX)
    useEffect(() => {
        // First, load any saved delivery info from localStorage (from cart page or previous selections)
        const savedDeliveryInfo = localStorage.getItem('selectedDeliveryInfo');
        const savedPincode = localStorage.getItem('selectedPincode');
        
        if (savedDeliveryInfo && savedPincode) {
            try {
                const parsedInfo = JSON.parse(savedDeliveryInfo);
                setDeliveryInfo(parsedInfo);
                setPostalCode(savedPincode);
                console.log('Loaded saved delivery info on mount:', parsedInfo, savedPincode);
            } catch (error) {
                console.error('Error parsing saved delivery info:', error);
            }
        }
        
        // Also check if shipping info already has delivery data
        if (shippingInfo.postalCode && shippingInfo.areaName) {
            setDeliveryInfo({
                area: shippingInfo.areaName,
                hubPlace: shippingInfo.hubPlace || 'Unknown Hub',
                deliveryCharge: 50 // Default charge
            });
            console.log('Loaded delivery info from shipping state:', shippingInfo);
        }
    }, [shippingInfo]);

    // Auto-fill form fields from user data - ONCE only
    useEffect(() => {
        // Only run this effect once when user data becomes available
        if (isAuthenticated && user && !fieldsAutoFilledRef.current) {
            console.log("Auto-filling with data from Redux store:", user);
            
            // Set the fields only if they're empty
            if (!address) {
                setAddress(user.address || '');
            }
            
            if (!phoneNo) {
                setPhoneNo(user.phone || '');
            }
            
            // Mark fields as auto-filled so we don't keep resetting them
            fieldsAutoFilledRef.current = true;
            
            // Try to fetch fresh data from the API as a backup, but don't update if user has entered data
            const fetchUserProfile = async () => {
                try {
                    console.log("Starting to fetch user profile data...");
                    setLoading(true);
                    
                    const config = {
                        headers: {
                            'Content-Type': 'application/json'
                        },
                        withCredentials: true
                    };
                    
                    // Try with myprofile endpoint (from your routes)
                    const { data } = await axios.get('/api/v1/myprofile', config);
                    console.log("Received user data from API:", data);
                    
                    if (data.success && data.user) {
                        console.log("Setting form fields with API user data");
                        // Only set these if they're still empty
                        if (!address) {
                            setAddress(data.user.address || '');
                        }
                        
                        if (!phoneNo) {
                            setPhoneNo(data.user.phone || '');
                        }
                    }
                } catch (error) {
                    console.error('Error fetching user profile:', error);
                    // Don't show error - we're already using Redux data
                } finally {
                    setLoading(false);
                }
            };
            
            fetchUserProfile();
        }
    }, [isAuthenticated, user, address, phoneNo]);

    // Validate pincode against our serviceable areas
    const validatePincode = async (pincode) => {
        try {
            const { data } = await axios.post('/api/v1/pincode/check', {
                pincode: pincode
            });
            
            return data;
        } catch (error) {
            console.error('Pincode validation error:', error);
            return {
                success: false,
                message: 'Error validating pincode'
            };
        }
    };

    // Check pincode delivery charges
    const checkPincode = async (pincode) => {
        if (!pincode || pincode.length !== 6) {
            setPincodeError('Please enter a valid 6-digit pincode');
            return false;
        }

        try {
            setPincodeLoading(true);
            setPincodeError('');

            const { data } = await axios.post('/api/v1/pincode/check', {
                pincode: pincode
            });

            if (data.success && data.isServiceable) {
                setDeliveryInfo(data.data);
                setPostalCode(pincode);

                // CRITICAL: Save to localStorage for persistence
                localStorage.setItem('selectedDeliveryInfo', JSON.stringify(data.data));
                localStorage.setItem('selectedPincode', pincode);

                toast.success(data.message);
                setShowManualInput(false);
                return true;
            } else {
                setPincodeError(data.message);
                setDeliveryInfo(null);
                // Clear localStorage on error
                localStorage.removeItem('selectedDeliveryInfo');
                localStorage.removeItem('selectedPincode');
                return false;
            }
        } catch (error) {
            console.error('Pincode check error:', error);
            const errorMessage = error.response?.data?.message || 'Error checking pincode';
            setPincodeError(errorMessage);
            setDeliveryInfo(null);
            return false;
        } finally {
            setPincodeLoading(false);
        }
    };

    // Handle dropdown selection
    const handleAreaSelect = async (selectedPincode) => {
        if (selectedPincode) {
            const success = await checkPincode(selectedPincode);
            if (success) {
                console.log('Successfully selected area for pincode:', selectedPincode);
            }
        } else {
            setDeliveryInfo(null);
            setPostalCode('');
            setPincodeError('');
            localStorage.removeItem('selectedDeliveryInfo');
            localStorage.removeItem('selectedPincode');
        }
    };

    // Handle manual pincode input
    const handleManualPincodeCheck = async () => {
        const success = await checkPincode(manualPincode);
        if (success) {
            setManualPincode('');
        }
    };

    // Simplified validation
    const isFormValid = Boolean(
        postalCode &&
        address.trim() &&
        phoneNo.length === 10 &&
        deliveryInfo // Must have valid delivery info
    );

    const submitHandler = async (e) => {
        e.preventDefault();
        
        if (!postalCode) {
            toast.error('Please select a delivery area');
            return;
        }
        
        if (!phoneNo || phoneNo.length !== 10) {
            toast.error('Please enter a valid 10-digit phone number');
            return;
        }
        
        if (!address.trim()) {
            toast.error('Please enter your delivery address');
            return;
        }

        if (!deliveryInfo) {
            toast.error('Please check delivery charges for the selected area');
            return;
        }
        
        // Validate pincode before proceeding
        const pincodeValidation = await validatePincode(postalCode);
        
        if (!pincodeValidation.success || !pincodeValidation.isServiceable) {
            toast.error(pincodeValidation.message || 'We do not deliver to this pincode');
            return;
        }
        
        // Save shipping info with delivery details
        dispatch(saveShippingInfo({
            address,
            city,
            phoneNo,
            postalCode,
            country,
            state,
            areaName: deliveryInfo.area,
            hubPlace: deliveryInfo.hubPlace
        }));
        
        navigate('/order/confirm');
    };

    return (
        <div
            className="min-h-screen"
            style={{
                backgroundImage: `url(https://meatbuddy-assets.s3.eu-north-1.amazonaws.com/other-images/meatbuddy-image-web-banner-background-5.png)`,
                backgroundRepeat: 'repeat',
                backgroundSize: 'auto',
                backgroundPosition: 'center'
            }}
        >
            <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <CheckoutSteps shipping />
                
                <div className="rounded-xl shadow-sm overflow-hidden p-6 mt-6 bg-white bg-opacity-90">
                    {/* Header */}
                    <div className="flex items-center gap-3 mb-6">
                        <Truck className="w-8 h-8 text-[#dc2626]" />
                        <h1 className="text-2xl font-bold text-gray-900 font-['Crimson_Text']">
                            Shipping Information
                        </h1>
                    </div>
                    
                    {/* Loading indicator */}
                    {loading && (
                        <div className="flex items-center justify-center p-4 mb-4 bg-gray-50 rounded-lg">
                            <div className="w-5 h-5 mr-3 border-2 border-t-2 border-gray-200 border-t-[#dc2626] rounded-full animate-spin"></div>
                            <p className="text-gray-600">Loading your information...</p>
                        </div>
                    )}
                    
                    {/* Warning Banner */}
                    <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
                        <div className="flex items-start">
                            <AlertTriangle className="w-5 h-5 text-yellow-400 mt-0.5 mr-3" />
                            <div>
                                <p className="text-sm text-yellow-700 font-medium">
                                    We currently deliver only to specific areas in Bangalore city.
                                </p>
                                <p className="text-xs text-yellow-600 mt-1">
                                    Delivery charge: ₹50 for all orders (no free delivery)
                                </p>
                            </div>
                        </div>
                    </div>

                    <form onSubmit={submitHandler} className="space-y-6">
                        {/* Enhanced PIN Code Selection with Persistence */}
                        <div>
                            <label htmlFor="postal_code_field" className="block text-sm font-medium text-gray-700 mb-1">
                                Select Delivery Area <span className="text-[#dc2626]">*</span>
                            </label>
                            
                            {/* Dropdown Selection */}
                            <div className="space-y-3">
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <MapPin className="h-5 w-5 text-gray-400" />
                                    </div>
                                    <select
                                        id="postal_code_field"
                                        value={postalCode}
                                        onChange={(e) => handleAreaSelect(e.target.value)}
                                        className="block w-full pl-10 py-2 border border-gray-300 rounded-lg focus:ring-[#dc2626] focus:border-[#dc2626] bg-white"
                                        required
                                    >
                                        <option value="">Select a delivery area</option>
                                        {servingAreas.map((area, index) => (
                                            <option key={`${area.code}-${index}`} value={area.code}>
                                                {area.area} - {area.code} (Hub: {area.hubPlace})
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* Manual Pincode Input Option */}
                                <div className="text-center">
                                    <button
                                        type="button"
                                        onClick={() => setShowManualInput(!showManualInput)}
                                        className="text-sm text-[#dc2626] hover:text-[#dc2626]/80 flex items-center justify-center gap-1 mx-auto transition-colors"
                                    >
                                        <Edit2 className="w-4 h-4" />
                                        {showManualInput ? 'Use dropdown instead' : 'Or enter pincode manually'}
                                    </button>
                                </div>

                                {/* Manual Pincode Input */}
                                {showManualInput && (
                                    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                                        <div className="flex justify-between items-center mb-3">
                                            <label className="block text-sm font-medium text-gray-700">
                                                Enter 6-digit Pincode
                                            </label>
                                            <button
                                                type="button"
                                                onClick={() => setShowManualInput(false)}
                                                className="text-gray-400 hover:text-gray-600"
                                            >
                                                <X className="w-4 h-4" />
                                            </button>
                                        </div>
                                        <div className="flex gap-2">
                                            <input
                                                type="text"
                                                maxLength="6"
                                                value={manualPincode}
                                                onChange={(e) => {
                                                    const value = e.target.value.replace(/\D/g, '');
                                                    setManualPincode(value);
                                                    setPincodeError('');
                                                }}
                                                className="flex-grow px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-[#dc2626] focus:border-[#dc2626]"
                                                placeholder="Enter pincode"
                                            />
                                            <button
                                                type="button"
                                                onClick={handleManualPincodeCheck}
                                                disabled={pincodeLoading || manualPincode.length !== 6}
                                                className="px-4 py-2 bg-[#dc2626] text-white text-sm rounded-lg hover:bg-[#dc2626]/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                                            >
                                                {pincodeLoading ? (
                                                    <RefreshCw className="w-4 h-4 animate-spin" />
                                                ) : (
                                                    'Check'
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Error Display */}
                            {pincodeError && (
                                <div className="flex items-start gap-2 text-red-600 text-sm mt-2">
                                    <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                                    <p>{pincodeError}</p>
                                </div>
                            )}

                            {/* Delivery Info Display */}
                            {deliveryInfo && (
                                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-3">
                                    <div className="flex items-start gap-2">
                                        <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                                        <div>
                                            <p className="font-medium text-sm text-green-800">
                                                ✅ Delivery Available for {deliveryInfo.area}
                                            </p>
                                            <p className="text-xs text-green-600 mt-1">
                                                📍 Hub: {deliveryInfo.hubPlace} | 🚚 Delivery Charge: ₹{deliveryInfo.deliveryCharge}
                                            </p>
                                            <p className="text-xs text-green-600">
                                                Pincode: {postalCode}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <p className="mt-1 text-xs text-gray-500">
                                Delivery charge: ₹50 for all areas
                            </p>
                        </div>

                        {/* Address Field */}
                        <div>
                            <label htmlFor="address_field" className="block text-sm font-medium text-gray-700 mb-1">
                                Detailed Address <span className="text-[#dc2626]">*</span>
                            </label>
                            <div className="relative">
                                <div className="absolute top-3 left-3">
                                    <Home className="h-5 w-5 text-gray-400" />
                                </div>
                                <textarea
                                    id="address_field"
                                    value={address}
                                    onChange={(e) => setAddress(e.target.value)}
                                    rows="3"
                                    className="block w-full pl-10 border border-gray-300 rounded-lg focus:ring-[#dc2626] focus:border-[#dc2626] bg-white"
                                    placeholder="Enter complete address with landmarks"
                                    required
                                />
                            </div>
                        </div>

                        {/* Phone Number */}
                        <div>
                            <label htmlFor="phone_field" className="block text-sm font-medium text-gray-700 mb-1">
                                Phone Number <span className="text-[#dc2626]">*</span>
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Phone className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    type="tel"
                                    id="phone_field"
                                    value={phoneNo}
                                    onChange={(e) => setPhoneNo(e.target.value.replace(/\D/g, '').slice(0, 10))}
                                    className="block w-full pl-10 py-2 border border-gray-300 rounded-lg focus:ring-[#dc2626] focus:border-[#dc2626] bg-white"
                                    placeholder="Enter 10-digit mobile number"
                                    required
                                />
                            </div>
                            <p className="mt-1 text-sm text-gray-500">
                                {phoneNo.length}/10 digits
                            </p>
                        </div>

                        {/* Location Info */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-gray-50 p-4 rounded-lg">
                            <div className="flex items-center gap-3">
                                <Building className="w-5 h-5 text-gray-400" />
                                <div>
                                    <p className="text-sm text-gray-500">City</p>
                                    <p className="font-medium">{city}</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <Map className="w-5 h-5 text-gray-400" />
                                <div>
                                    <p className="text-sm text-gray-500">State</p>
                                    <p className="font-medium">{state}</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <Globe className="w-5 h-5 text-gray-400" />
                                <div>
                                    <p className="text-sm text-gray-500">Country</p>
                                    <p className="font-medium">{country}</p>
                                </div>
                            </div>
                        </div>

                        {/* Submit Button */}
                        <button
                            type="submit"
                            className={`w-full py-3 px-4 rounded-lg font-medium flex items-center justify-center
                                ${isFormValid
                                    ? 'bg-[#dc2626] text-white hover:bg-[#dc2626]/90'
                                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                } transition-colors`}
                            disabled={!isFormValid}
                        >
                            Proceed to Confirm Order
                            <ArrowRight className="w-5 h-5 ml-2" />
                        </button>

                        {/* Form Requirements */}
                        <div className="text-sm text-gray-500">
                            <p>* Required fields</p>
                            <ul className="mt-2 list-disc list-inside">
                                <li>Select a delivery area and verify delivery charges</li>
                                <li>Enter your complete delivery address</li>
                                <li>Provide a valid 10-digit mobile number</li>
                            </ul>
                            <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                <p className="font-medium text-blue-800">Delivery Information:</p>
                                <ul className="mt-1 text-blue-700 text-xs">
                                    <li>• Fixed delivery charge: ₹50 for all orders</li>
                                    <li>• No free delivery available</li>
                                    <li>• Delivery within 1 hour during business hours</li>
                                </ul>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default Shipping;